/**
 * TrackController — effect-only child component for breadcrumb track
 * and range rings.
 *
 * Extracted from MapView.tsx as part of the Claude audit 2026-05-18
 * finding 7 part 2 (component split). Owns its own narrow zustand
 * subscriptions so its effects only re-run when track/ring-related
 * state changes, not on every MapView render.
 *
 * Also exports `pushTrack` so MapView's init effect can seed the
 * 'track' source on cold-start and basemap-switch without reaching
 * into this module's internals.
 *
 * Returns null — no DOM output.
 */
import { useEffect } from 'react';
import type { GeoJSONSource } from 'maplibre-gl';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useTrackStore } from '../../stores/trackStore';
import { useGpsStore } from '../../stores/gpsStore';
import { geodesicCircleRing } from '../../lib/geo';
import { useLayerVisibility } from '../useLayerEffects';
import { getMapInstance, EMPTY_FC, safeGetSource } from '../MapView';
import { registerStyleRebuild } from '../layerVisibilityRegistry';
import type { Map as MlMap } from 'maplibre-gl';
import React from 'react';

// ---------------------------------------------------------------------------
// Module-level O(1) append cache for pushTrack.
//
// (Claude audit 2026-05-18, finding 5) Append-only coordinate cache so
// pushTrack does O(1) work per new GPS fix instead of O(N) where N is
// the entire recorded track. For an hour-long flight at 1 Hz that's
// 3600 points re-mapped on every push; with the 500 ms throttle and
// the 1 Hz GPS, the old code was rebuilding the full LineString twice
// per second forever.
// ---------------------------------------------------------------------------
let trackCoordCache: [number, number][] = [];
let trackCoordCacheLen = 0;
const trackFC: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: trackCoordCache },
    },
  ],
};

export function pushTrack(map: MlMap) {
  const { points } = useTrackStore.getState();
  const src = safeGetSource<GeoJSONSource>(map, 'track');
  if (!src) return;
  if (points.length < 2) {
    trackCoordCache.length = 0;
    trackCoordCacheLen = 0;
    src.setData(EMPTY_FC);
    return;
  }
  // Detect array replacement (start/stop/clear). When the store's
  // points array shrunk or wasn't a strict extension of our cache,
  // rebuild from scratch. Otherwise append only the new tail.
  if (points.length < trackCoordCacheLen) {
    trackCoordCache.length = 0;
    trackCoordCacheLen = 0;
  }
  for (let i = trackCoordCacheLen; i < points.length; i++) {
    const p = points[i];
    trackCoordCache.push([p.lon, p.lat]);
  }
  trackCoordCacheLen = points.length;
  // The trackFC's geometry.coordinates IS trackCoordCache (same
  // reference); we mutate it in place and call setData on the same FC
  // object. MapLibre clones internally so the source reads its own
  // copy from this point.
  src.setData(trackFC);
}

// ---------------------------------------------------------------------------
// Range-ring geometry helpers (moved here from MapView.tsx).
// ---------------------------------------------------------------------------

/**
 * ForeFlight range-ring ladder. Each tuple is (inner / middle / outer)
 * in nautical miles. The OUTER ring is the design anchor: ForeFlight
 * sizes it to sit roughly half-way between the ownship and the screen
 * edge. We pick the ladder bucket whose outer radius is closest to
 * that target for the CURRENT viewport, which auto-scales the rings
 * down on iPhones and up on iPads / desktops without per-device
 * branches.
 *
 * Order: closest-in first (used at chart-native zoom) → continental
 * scale last.
 */
const RING_LADDER: ReadonlyArray<readonly [number, number, number]> = [
  [2, 4, 10],
  [3, 6, 15],
  [5, 10, 25],
  [10, 20, 50],
  [15, 30, 75],
  [20, 40, 100],
  [40, 80, 200],
  [80, 160, 400],
  [160, 320, 800],
  [320, 640, 1600],
  [400, 800, 2000],
];

/**
 * Target ratio: outer ring radius ÷ half-of-smallest-viewport-dimension.
 * 0.55 keeps the outer ring just inside the viewport edge on every
 * device, leaving the middle + inner rings well-inside for at-a-glance
 * distance reads on nearby waypoints. ForeFlight's effective ratio on
 * an iPad sits around 0.5-0.6 depending on orientation.
 */
const OUTER_RING_VIEWPORT_RATIO = 0.55;

function pickRingRadii(zoom: number, minViewportPx: number, latDeg: number): number[] {
  // Web Mercator meters / px at this latitude.
  const latRad = (latDeg * Math.PI) / 180;
  const metersPerPx =
    (156543.03392 * Math.cos(latRad)) / Math.pow(2, zoom);
  const nmPerPx = metersPerPx / 1852;
  // Half the smallest viewport dimension in NM — distance from the
  // ownship (assumed centered) to the nearest edge of the screen.
  const halfViewNm = (minViewportPx / 2) * nmPerPx;
  const targetOuter = halfViewNm * OUTER_RING_VIEWPORT_RATIO;
  // Pick the ladder bucket whose outer radius is closest to target on
  // a log scale (so 50 nm and 100 nm are treated as equidistant from
  // 70 nm).
  let bestIdx = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < RING_LADDER.length; i++) {
    const outer = RING_LADDER[i][2];
    const d = Math.abs(Math.log(outer) - Math.log(Math.max(1, targetOuter)));
    if (d < bestDiff) { bestDiff = d; bestIdx = i; }
  }
  const [a, b, c] = RING_LADDER[bestIdx];
  return [a, b, c];
}

function formatRingLabel(nm: number): string {
  if (nm < 1) return nm.toFixed(1) + ' nm';
  return Math.round(nm) + ' nm';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// HI-1 step 3 (2026-05-23): rebuildRef prop removed. The controller now
// owns its style-rebuild restore registration via
// layerVisibilityRegistry; MapView no longer needs to prop-drill a ref.
export function TrackController() {
  const showBreadcrumbs = useLayerVisibilityStore((s) => s.layers.breadcrumbs);
  const showRangeRings  = useLayerVisibilityStore((s) => s.layers.rangeRings);

  // ── Breadcrumb track ─────────────────────────────────────────────────────
  //
  // Track log
  //
  // F7 (2026-05-16 perf audit): rebuild gate. pushTrack() rebuilds the
  // entire LineString (every point.map -> [lon,lat]) on every fix,
  // tessellating up to thousands of coords every second during cruise.
  // Skip the rebuild when (a) the breadcrumb layer is hidden in the UI
  // (the rendered output is invisible anyway) and (b) when neither the
  // point count nor a 0.5s clock has changed since last push. The active
  // segment still reaches the source within half a second of any new fix,
  // which is faster than human perception.
  useEffect(() => {
    let lastPushAt = 0;
    let lastPointCount = -1;
    const unsub = useTrackStore.subscribe((state) => {
      const map = getMapInstance();
      if (!map) return;
      const breadcrumbsOn = useLayerVisibilityStore.getState().layers.breadcrumbs;
      if (!breadcrumbsOn) {
        // Layer hidden — the GPS track is still recorded in the store,
        // we just don't update the GeoJSON source until the user toggles
        // the layer back on.
        return;
      }
      const pointCount = state.points.length;
      const now = performance.now();
      // Only rebuild when something visible has changed. Fall through if
      // 500ms have elapsed (so the active segment stays current) OR if
      // the point-count delta is non-zero.
      if (pointCount === lastPointCount && now - lastPushAt < 500) return;
      lastPointCount = pointCount;
      lastPushAt = now;
      pushTrack(map);
    });
    return () => unsub();
  }, []);

  // Breadcrumb visibility — gates the track-line layer on the user's
  // 'breadcrumbs' setting (Settings → Display, also available as a
  // layer toggle). When OFF the GPS track is still recorded; only the
  // map rendering is hidden.
  useLayerVisibility(() => getMapInstance(), ['track-line'], showBreadcrumbs);

  // ── Range rings ──────────────────────────────────────────────────────────

  // Range rings around the ownship — zoom-adaptive concentric circles
  // so the pilot can eyeball distance from any waypoint at a glance.
  // Visibility is gated separately so users who don't want them can
  // disable in Settings.
  useLayerVisibility(
    () => getMapInstance(),
    ['range-rings-line', 'range-rings-label'],
    showRangeRings,
  );

  // Recompute range-ring geometry whenever the GPS fix moves, the
  // zoom changes, or the toggle flips back on. Three concentric
  // geodesic circles whose radii scale with zoom (see pickRingRadii).
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;

    function rebuild() {
      if (!map) return;
      const src = safeGetSource<GeoJSONSource>(map, 'range-rings');
      if (!src) return;
      const fix = useGpsStore.getState().fix;
      if (!fix || !showRangeRings) {
        src.setData(EMPTY_FC);
        return;
      }
      const z = map.getZoom();
      // Use the smaller viewport dimension so the outer ring stays
      // visible on portrait iPhones (height < width on landscape
      // tablets but width < height on phones). Falls back to 1100 px
      // when we can't read the canvas (test envs).
      const canvas = map.getCanvas();
      const minDimPx = canvas
        ? Math.min(canvas.clientWidth, canvas.clientHeight)
        : 1100;
      const radiiNm = pickRingRadii(z, minDimPx, fix.lat);
      const features: GeoJSON.Feature[] = [];
      for (const r of radiiNm) {
        const ring = geodesicCircleRing(fix.lat, fix.lon, r, 72);
        features.push({
          type: 'Feature',
          properties: { radiusNm: r, kind: 'ring' },
          geometry: { type: 'LineString', coordinates: ring },
        });
        // Label placed due east of the ownship at the ring radius so
        // the user always has a numeric scale reference.
        const labelPt = ring[Math.floor(ring.length * 0.25)]; // ~90° bearing
        features.push({
          type: 'Feature',
          properties: { label: `${formatRingLabel(r)}`, kind: 'label' },
          geometry: { type: 'Point', coordinates: labelPt },
        });
      }
      src.setData({ type: 'FeatureCollection', features });
    }

    // HI-1 step 2/3: style-rebuild restore is registered with the
    // central registry; MapView's styledata + map.on('load') handlers
    // iterate it via runStyleRebuildHandlers.
    const unregister = registerStyleRebuild({
      id: 'rangeRings',
      restore: () => rebuild(),
    });
    rebuild();
    const onChange = () => rebuild();
    map.on('zoom', onChange);
    map.on('resize', onChange);
    // F5 (2026-05-16 perf audit): movement-magnitude gate on the GPS
    // subscribe. Skip the 72-vertex × 3-ring tessellation when the fix
    // has only shifted by sub-100m / sub-0.1-zoom — the ring shift is
    // imperceptible at any sane viewport and the worker time is better
    // spent on basemap tiles. Pattern lifted from GlideRingLayer:178-186.
    let lastRingFix: { lat: number; lon: number; zoom: number } = { lat: NaN, lon: NaN, zoom: NaN };
    const unsubGps = useGpsStore.subscribe((s) => {
      const fix = s.fix;
      if (!fix) return;
      const z = map.getZoom();
      const dLat = Math.abs(fix.lat - lastRingFix.lat);
      const dLon = Math.abs(fix.lon - lastRingFix.lon);
      const dZ  = Math.abs(z - lastRingFix.zoom);
      // ~100m ≈ 0.001° lat; 0.001° lon is finer than 100m at low lat but
      // the gate is symmetric so the conservative case wins.
      if (dLat < 0.001 && dLon < 0.001 && dZ < 0.1) return;
      lastRingFix = { lat: fix.lat, lon: fix.lon, zoom: z };
      rebuild();
    });
    return () => {
      map.off('zoom', onChange);
      map.off('resize', onChange);
      unsubGps();
      unregister();
    };
  }, [showRangeRings]);

  return null;
}
